import { type CSSProperties, memo, type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import Imgix, { type SharedImgixAndSourceProps } from 'react-imgix';

import { isValidUrl } from '@air/utilities';

import {
  buildImgixSignRequestUrl,
  getImgixSignPath,
  isByosOriginalCloudFrontUrl,
  isImgixSignRequestUrl,
  isSignedImgixUrl,
  needsImgixServerSign,
  resolveByosImgixSrc,
} from './byosImgixUrls';
import { isImgixUrl } from './isImgixUrl';

export type ImgixImageProps = {
  /** The alt text for the image - this should be passed in for accessibility purposes */
  alt: string;
  /** This is the backup URL of the image in the event that Imgix is down */
  fallbackUrl?: string;
  /**
   * Fires when the Imgix rendition fails, before the plain-image fallback.
   * Return true when the caller will request a different Imgix `src` (so this component should not swap to `fallbackUrl` yet).
   */
  onRenditionError?: () => boolean;
  /** Use this callback to fire when either the Imgix or fallback img loads successfully */
  onLoad?: (e?: SyntheticEvent<HTMLImageElement>) => void;
  /** Use to disable drag which is true by default- needed for image annotations */
  draggable?: boolean;
  style?: CSSProperties;
} & SharedImgixAndSourceProps;

const DEFAULT_IMGIX_PARAMS: SharedImgixAndSourceProps['imgixParams'] = { auto: 'format' };

type ImgHtmlAttributes = {
  alt: string;
  draggable: boolean;
  onLoad?: (e?: SyntheticEvent<HTMLImageElement>) => void;
  style?: CSSProperties;
};

const renderFallbackImg = ({
  fallbackUrl,
  htmlAttributes,
}: {
  fallbackUrl: string;
  htmlAttributes: ImgHtmlAttributes;
}) => (
  // biome-ignore lint/performance/noImgElement: fallback for when imgix is unavailable
  // biome-ignore lint/a11y/useAltText: alt is included in htmlAttributes
  <img {...htmlAttributes} src={fallbackUrl} />
);

const renderPlainImg = ({
  alt,
  className,
  height,
  htmlAttributes,
  onError,
  sizes,
  src,
  width,
}: {
  alt: string;
  className?: string;
  height?: SharedImgixAndSourceProps['height'];
  htmlAttributes: ImgHtmlAttributes;
  onError?: (err: unknown) => void;
  sizes?: SharedImgixAndSourceProps['sizes'];
  src: string;
  width?: SharedImgixAndSourceProps['width'];
}) => (
  // biome-ignore lint/performance/noImgElement: relative, signed, or non-imgix URLs cannot go through react-imgix
  <img
    {...htmlAttributes}
    alt={alt}
    className={className}
    height={height}
    onError={onError}
    sizes={sizes}
    src={src}
    width={width}
  />
);

/**
 * This component is a wrapper around the react-imgix component and it's purpose is to allow a fallback url (`fallbackUrl`) to load in the event that the Imgix service is down. You'd be surprised how often this happens.
 * All props are spread onto the react-imgix component with the exception of fallbackUrl and onError.
 */
export const ImgixImage = memo(
  ({
    fallbackUrl = '',
    onRenditionError,
    onLoad,
    alt,
    draggable = true,
    style,
    imgixParams,
    ...restOfProps
  }: ImgixImageProps) => {
    const [error, setError] = useState(false);

    const resolvedSrc = useMemo(() => resolveByosImgixSrc(restOfProps.src), [restOfProps.src]);
    const isSignRequest = useMemo(() => isImgixSignRequestUrl(restOfProps.src), [restOfProps.src]);
    const isUrl = useMemo(() => isValidUrl(resolvedSrc), [resolvedSrc]);
    const isImgix = useMemo(() => isImgixUrl(resolvedSrc), [resolvedSrc]);
    const signPath = getImgixSignPath();
    const shouldServerSign = needsImgixServerSign(resolvedSrc) && !!signPath;

    const htmlAttributes = useMemo(
      () => ({
        alt,
        draggable,
        onLoad,
        style,
      }),
      [alt, style, draggable, onLoad],
    );

    const memoizedImgixParams = useMemo(
      () => (typeof imgixParams === 'undefined' ? DEFAULT_IMGIX_PARAMS : { ...DEFAULT_IMGIX_PARAMS, ...imgixParams }),
      [imgixParams],
    );

    const handleError = useCallback(
      (err: unknown) => {
        if (onRenditionError?.()) {
          return;
        }

        setError(!!err);
      },
      [onRenditionError],
    );

    const fallbackImg = fallbackUrl ? renderFallbackImg({ fallbackUrl, htmlAttributes }) : null;
    const plainImgProps = {
      alt,
      className: restOfProps.className,
      height: restOfProps.height,
      htmlAttributes,
      sizes: restOfProps.sizes,
      width: restOfProps.width,
    };

    if (!isUrl && !isSignRequest) {
      return null;
    }

    if (isSignRequest) {
      if (error) {
        return fallbackImg;
      }

      return renderPlainImg({ ...plainImgProps, onError: handleError, src: restOfProps.src });
    }

    // Handshake-locked original CF must never load as a public <img>.
    if (isByosOriginalCloudFrontUrl(restOfProps.src) && !isImgix) {
      return null;
    }

    if (shouldServerSign && signPath) {
      const signedSrc = buildImgixSignRequestUrl({
        params: {
          ...memoizedImgixParams,
          ...(restOfProps.width ? { w: restOfProps.width } : {}),
          ...(restOfProps.height ? { h: restOfProps.height } : {}),
        },
        signPath,
        src: resolvedSrc,
      });

      if (error) {
        return fallbackImg;
      }

      return renderPlainImg({ ...plainImgProps, onError: handleError, src: signedSrc });
    }

    if (!isImgix) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`ImgixImage: src "${restOfProps.src}" is not an imgix URL. imgix transformations will not apply.`);
      }

      return renderPlainImg({ ...plainImgProps, src: restOfProps.src });
    }

    if (isSignedImgixUrl(resolvedSrc)) {
      if (error) {
        return fallbackImg;
      }

      return renderPlainImg({ ...plainImgProps, onError: handleError, src: resolvedSrc });
    }

    if (error) {
      return fallbackImg;
    }

    return (
      <Imgix
        {...restOfProps}
        htmlAttributes={{
          ...htmlAttributes,
          onError: handleError,
        }}
        imgixParams={memoizedImgixParams}
        src={resolvedSrc}
      />
    );
  },
);

ImgixImage.displayName = 'ImgixImage';
